import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Radar, Flame, CheckCircle2, Zap } from "lucide-react";
import { GuestWantForm } from "@/components/GuestWantForm";

const timeAgo = (iso) => {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 3600) return "just now";
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

export default function OpenDemands() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/match/open-demands").then(({ data }) => setData(data)).catch(() => setData({ demands: [], unfilled_count: 0 }));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14" data-testid="open-demands-page">
      <div className="text-xs uppercase tracking-widest text-[#FF3B30] flex items-center gap-2">
        <Radar className="h-3.5 w-3.5" /> Live buyer demand
      </div>
      <h1 className="mt-2 font-black tracking-tight text-4xl sm:text-5xl">
        Buyers are waiting.<br />
        <span className="text-[#FF3B30]">Nobody has what they want.</span>
      </h1>
      <p className="mt-4 text-neutral-400 leading-relaxed max-w-xl">
        These are real, live requests from buyers on VISMARKS. Every red one is a sale
        nobody can fulfil yet. List the product and the buyer is <span className="text-white font-semibold">alerted within seconds</span>.
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Link to="/register?role=seller">
          <Button data-testid="open-demands-cta" className="rounded-full bg-[#FF3B30] hover:bg-[#e0342a] text-white h-11 px-7 font-bold">
            <Zap className="h-4 w-4 mr-2" /> Become a seller &amp; fill a demand
          </Button>
        </Link>
        {data && data.unfilled_count > 0 && (
          <span className="text-sm text-neutral-400" data-testid="unfilled-count">
            <span className="text-[#FF3B30] font-bold">{data.unfilled_count}</span> unfilled demand{data.unfilled_count === 1 ? "" : "s"} right now
          </span>
        )}
      </div>

      <div className="mt-8">
        <GuestWantForm compact initialText={params.get("want") ? `Something like: ${params.get("want")}` : ""} />
      </div>

      <div className="mt-10 space-y-2">
        {data === null && <div className="text-sm text-neutral-500">Loading live demand…</div>}
        {data?.demands.length === 0 && (
          <div className="text-sm text-neutral-500 rounded-xl border border-dashed border-[#27272A] p-8 text-center">
            All quiet right now — check back soon, buyers post new wants daily.
          </div>
        )}
        {data?.demands.map((d, i) => (
          <div key={i} data-testid={`open-demand-${i}`} className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${d.unfilled ? "border-[#FF3B30]/40 bg-[#FF3B30]/[0.04]" : "border-[#27272A] bg-[#121212]"}`}>
            {d.unfilled
              ? <Flame className="h-4 w-4 text-[#FF3B30] flex-shrink-0" />
              : <CheckCircle2 className="h-4 w-4 text-[#34C759] flex-shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">"{d.text}"</div>
              <div className="text-xs text-neutral-500">
                {d.max_price != null ? `Budget up to $${d.max_price}` : "Any budget"} · {timeAgo(d.created_at)}
                {d.unfilled
                  ? <span className="text-[#FF3B30] font-semibold"> · UNFILLED — be the first</span>
                  : <span className="text-[#34C759]"> · {d.matches_total} match{d.matches_total === 1 ? "" : "es"} live</span>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6 text-sm text-neutral-400">
        <span className="text-white font-semibold">How it works:</span> join as a seller, list your products
        (founding sellers get their first month free with unlimited listings), and our AI instantly connects them to the buyers already asking.
        Buying something instead? <Link to="/wanted" className="text-[#007AFF] hover:underline">Post what you want</Link>.
      </div>
    </div>
  );
}
